import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Users, Save, X, Loader2 } from "lucide-react";

export default function AgentGroups() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", description: "", agent_ids: new Set() });
  const [editing, setEditing] = useState(null);

  const groups = useQuery({
    queryKey: ["agent-groups"],
    queryFn: () => api.get("/agent-groups").then(r => r.data),
  });
  const agents = useQuery({
    queryKey: ["users-for-groups"],
    queryFn: () => api.get("/users").then(r => r.data.filter(u => u.role === "agent")),
  });

  const create = useMutation({
    mutationFn: (body) => api.post("/agent-groups", body),
    onSuccess: () => {
      setForm({ name: "", description: "", agent_ids: new Set() });
      toast.success("Group created");
      qc.invalidateQueries({ queryKey: ["agent-groups"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const update = useMutation({
    mutationFn: ({id, body}) => api.patch(`/agent-groups/${id}`, body),
    onSuccess: () => {
      setEditing(null);
      toast.success("Group updated");
      qc.invalidateQueries({ queryKey: ["agent-groups"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/agent-groups/${id}`),
    onSuccess: () => { toast.success("Group deleted"); qc.invalidateQueries({ queryKey: ["agent-groups"] }); },
  });

  const toggleAgent = (setter, currSet, id) => {
    const next = new Set(currSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  };

  const submit = () => {
    if (!form.name.trim()) { toast.error("Group name required"); return; }
    create.mutate({
      name: form.name.trim(),
      description: form.description.trim(),
      agent_ids: [...form.agent_ids],
    });
  };

  return (
    <Layout title="Agent Groups." subtitle="Cluster agents so campaigns can target specific squads">
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-white/10">
          {groups.isLoading && <div className="p-6 text-zinc-500 text-sm">Loading…</div>}
          {groups.data?.length === 0 && (
            <div className="p-10 text-center">
              <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// no groups yet</div>
              <div className="text-zinc-400 text-sm">Create a group to assign campaigns to a specific set of agents (e.g. Mumbai squad, or Warmed accounts only).</div>
            </div>
          )}
          {groups.data?.map((g, i) => {
            const isEditing = editing?.id === g.id;
            return (
              <div key={g.id} data-testid={`group-row-${g.id}`} className={`px-5 py-4 hover:bg-[#0e0e10] ${i > 0 ? "border-t border-white/5" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="w-4 h-4 text-[#FF3B30]"/>
                      <div className="font-display font-black text-lg tracking-tight">{g.name}</div>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border border-white/10 px-1.5 py-0.5">{g.agent_count} agent(s)</span>
                    </div>
                    {g.description && <div className="text-[12px] text-zinc-400 mb-2">{g.description}</div>}
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(g.agents || []).map(a => (
                        <span key={a.id} className="text-[10px] px-1.5 py-0.5 border border-white/10 text-zinc-300">{a.name}</span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => setEditing(isEditing ? null : { id: g.id, name: g.name, description: g.description || "", agent_ids: new Set(g.agent_ids || []) })}
                    data-testid={`edit-group-${g.id}`}
                    className="p-1.5 border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white text-[10px] font-mono uppercase tracking-widest px-2"
                  >{isEditing ? "close" : "edit"}</button>
                  <button
                    onClick={() => { if (window.confirm(`Delete group ${g.name}?`)) del.mutate(g.id); }}
                    data-testid={`delete-group-${g.id}`}
                    className="p-1.5 border border-white/10 hover:border-[#FF3B30] text-zinc-400 hover:text-[#FF3B30]"
                  >
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
                {isEditing && (
                  <div className="mt-3 border border-[#FF3B30]/40 bg-[#050505] p-3" data-testid={`edit-group-panel-${g.id}`}>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Name</label>
                    <input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})}
                           data-testid={`edit-group-name-${g.id}`}
                           className="w-full bg-[#0a0a0c] border border-white/10 px-3 py-2 mb-3 text-[13px]"/>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Description</label>
                    <input value={editing.description} onChange={(e) => setEditing({...editing, description: e.target.value})}
                           className="w-full bg-[#0a0a0c] border border-white/10 px-3 py-2 mb-3 text-[13px]"/>
                    <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Agents ({editing.agent_ids.size})</label>
                    <div className="grid grid-cols-2 gap-1.5 mb-3 max-h-[240px] overflow-y-auto border border-white/10 p-2 bg-[#0a0a0c]">
                      {(agents.data || []).map(a => (
                        <label key={a.id} className={`flex items-center gap-2 px-2 py-1 border cursor-pointer text-[11px] ${
                          editing.agent_ids.has(a.id) ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"
                        }`}>
                          <input type="checkbox" checked={editing.agent_ids.has(a.id)}
                                 onChange={() => toggleAgent((v) => setEditing({...editing, agent_ids: v}), editing.agent_ids, a.id)}
                                 data-testid={`edit-group-agent-${g.id}-${a.id}`}
                                 className="accent-[#FF3B30]"/>
                          <span className="truncate">{a.name}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing(null)} className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 border border-white/10 px-3 py-1.5 hover:text-white">cancel</button>
                      <button
                        onClick={() => update.mutate({ id: g.id, body: {
                          name: editing.name, description: editing.description, agent_ids: [...editing.agent_ids],
                        }})}
                        disabled={update.isPending}
                        data-testid={`save-group-${g.id}`}
                        className="text-[11px] font-mono uppercase tracking-widest bg-[#FF3B30] hover:bg-[#FF5A50] text-white px-3 py-1.5 disabled:opacity-50"
                      >{update.isPending ? "saving…" : "save"}</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="border border-white/10 p-5 h-fit">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Create group</div>
          <h3 className="font-display font-black text-xl tracking-tight mb-4">New squad.</h3>
          <input
            placeholder="Group name (e.g. Mumbai warmed)"
            value={form.name}
            onChange={(e)=>setForm({...form, name:e.target.value})}
            data-testid="new-group-name"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-sm"
          />
          <input
            placeholder="Description (optional)"
            value={form.description}
            onChange={(e)=>setForm({...form, description:e.target.value})}
            data-testid="new-group-desc"
            className="w-full bg-[#050505] border border-white/10 px-3 py-2 mb-3 text-sm"
          />
          <div className="mb-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Agents ({form.agent_ids.size})</div>
            <div className="grid gap-1 max-h-[260px] overflow-y-auto border border-white/10 p-2 bg-[#050505]">
              {(agents.data || []).map(a => (
                <label key={a.id} className={`flex items-center gap-2 px-2 py-1 border cursor-pointer text-[11px] ${
                  form.agent_ids.has(a.id) ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"
                }`}>
                  <input type="checkbox" checked={form.agent_ids.has(a.id)}
                         onChange={() => toggleAgent((v) => setForm({...form, agent_ids: v}), form.agent_ids, a.id)}
                         data-testid={`new-group-agent-${a.id}`}
                         className="accent-[#FF3B30]"/>
                  <span className="truncate">{a.name} <span className="text-zinc-500 text-[10px]">· {a.email}</span></span>
                </label>
              ))}
              {(agents.data || []).length === 0 && <div className="text-[11px] text-zinc-600 italic px-2 py-2">no agents yet</div>}
            </div>
          </div>
          <button
            onClick={submit}
            disabled={create.isPending}
            data-testid="create-group-btn"
            className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {create.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
            CREATE GROUP
          </button>
        </div>
      </div>
    </Layout>
  );
}
